import dayjs from "dayjs";
import { jsPDF } from "jspdf";
import { autoTable, type HookData, type CellHookData } from "jspdf-autotable";

import {
  EventStatus,
  TestEventType,
  TestingToolWithAll,
  TestingTool,
  TestStepStatus
} from "./testingTools.types";

export const maxTimeToWaitForPingEventSec = 30;
export const eventFeedReportMargin = 15;

export const createEventFeedReportByTestCategory = (
  testNameOrigin: TestingToolWithAll,
  completeEventFeed: {
    text: string;
    state: EventStatus;
    time: Date;
    testOrigin: TestingTool;
  }[],
  document: jsPDF,
  startingLineCoordinate: number
): { nextLineCoordinateCount: number } => {
  const pageHeight = document.internal.pageSize.height;
  const approximateRowHeight = 16;
  const eventFeedCategoryTitleHeight = 12;
  // category test subtitles, e.g: `Successful Test Actions`
  const eventFeedCategorySubtitleHeight = 11;

  let eventFeedCategory = completeEventFeed.slice();
  if (testNameOrigin !== "All") {
    eventFeedCategory = completeEventFeed.filter(
      (event) => event.testOrigin === testNameOrigin
    );
  }

  if (!eventFeedCategory.length)
    return { nextLineCoordinateCount: startingLineCoordinate };

  const eventFeedCategoryContent = eventFeedCategory.map((eventData) => [
    dayjs(eventData.time).format("YYYY-MM-DD, h:mm:ss a"),
    eventData.state,
    eventData.text,
    eventData.testOrigin
  ]);

  let successEventsContent;
  let failedEventsContent;
  let infoEventsContent;
  if (testNameOrigin !== "All") {
    successEventsContent = eventFeedCategoryContent.filter((eventContentRow) =>
      eventContentRow.includes("Success")
    );
    failedEventsContent = eventFeedCategoryContent.filter((eventContentRow) =>
      eventContentRow.includes("Failed")
    );
    infoEventsContent = eventFeedCategoryContent.filter((eventContentRow) =>
      eventContentRow.includes("Info")
    );
  }
  const headers = [
    "Timestamp",
    "Event Type",
    "Event Details",
    ...(testNameOrigin === "All" ? ["Test"] : [])
  ];
  let currentLineCoordinateCount = startingLineCoordinate;

  let eventFeedCategoryTitle;
  if (testNameOrigin !== "All") {
    eventFeedCategoryTitle = `${testNameOrigin} Test Session Summary`;
  } else {
    eventFeedCategoryTitle = "Full Event Feed";
  }

  const eventFeedCategoryContentToDisplay: {
    content: string[][];
    eventFeedContentType: string;
  }[] = [
    ...(successEventsContent && successEventsContent?.length
      ? [
          {
            content: successEventsContent,
            eventFeedContentType: "Successful Test Actions"
          }
        ]
      : []),
    ...(failedEventsContent && failedEventsContent?.length
      ? [
          {
            content: failedEventsContent,
            eventFeedContentType: "Failed Test Actions"
          }
        ]
      : []),
    ...(infoEventsContent && infoEventsContent?.length
      ? [
          {
            content: infoEventsContent,
            eventFeedContentType: "Informational Test Messages"
          }
        ]
      : []),
    ...(testNameOrigin === "All"
      ? [
          {
            content: eventFeedCategoryContent,
            eventFeedContentType: "All"
          }
        ]
      : [])
  ];

  const eventFeedCategoryTitleHeightWithMargin =
    eventFeedCategoryTitleHeight + 5;
  //complete event feed has no subtitle(s)
  const testCategorySubtitleHeightWithMargin =
    testNameOrigin !== "All" ? eventFeedCategorySubtitleHeight + 6 : 0;
  const titleAndSubtitleTotalHeight =
    eventFeedCategoryTitleHeightWithMargin +
    testCategorySubtitleHeightWithMargin;

  //if we can't fit at least test title, subtitle and first 2 rows of a table
  //then add new page and move the text onto the newly created page
  if (
    currentLineCoordinateCount +
      titleAndSubtitleTotalHeight +
      approximateRowHeight * 2 >
    pageHeight - eventFeedReportMargin
  ) {
    document.addPage();
    currentLineCoordinateCount = eventFeedReportMargin;
  }

  document.setFontSize(eventFeedCategoryTitleHeight);
  document.setFont("helvetica", "bold");
  document.text(eventFeedCategoryTitle, 10, currentLineCoordinateCount);
  currentLineCoordinateCount += 5;

  eventFeedCategoryContentToDisplay.forEach(
    ({ content, eventFeedContentType }, eventFeedContentIndex) => {
      const currentTableRowHeights: number[] = [];

      if (eventFeedContentType !== "All") {
        if (eventFeedContentIndex === 0) currentLineCoordinateCount += 3;
        document.setFont("helvetica", "normal");
        document.setFontSize(eventFeedCategorySubtitleHeight);
        document.text(eventFeedContentType, 12, currentLineCoordinateCount);
        currentLineCoordinateCount += 3;
      }
      let finalTableLineCoordinate = 0;
      autoTable(document, {
        head: [headers],
        body: content,
        startY: currentLineCoordinateCount,
        margin: { left: 12, right: eventFeedReportMargin },
        styles: {
          halign: "center",
          cellPadding: 3,
          fontSize: 10
        },
        headStyles: {
          fillColor: [232, 232, 232],
          textColor: [33, 33, 33],
          lineWidth: 0.3,
          lineColor: 220
        },
        bodyStyles: {
          lineWidth: 0.3,
          lineColor: 220
        },
        didDrawPage: (data: HookData) => {
          finalTableLineCoordinate = data.cursor?.y ?? 0;
        },
        didDrawCell: (data: CellHookData) => {
          if (data.section === "body" || data.section === "head") {
            const rowHeight = data.row.height;
            const rowIndex = data.row.index;
            //keep track of the exact height of each row
            if (!currentTableRowHeights[rowIndex]) {
              currentTableRowHeights[rowIndex] = rowHeight;
            }
          }
        }
      });

      currentTableRowHeights.forEach((height: number) => {
        currentLineCoordinateCount += height;
      });
      //if calculated height is greater than page height it means the table has been rendered on a new page
      //this means we have to use the coordinate of the newly created page
      if (currentLineCoordinateCount > pageHeight - eventFeedReportMargin) {
        currentLineCoordinateCount = finalTableLineCoordinate;
      }
      currentLineCoordinateCount += 20;
    }
  );

  return { nextLineCoordinateCount: currentLineCoordinateCount };
};

export const getEventTypeFromTestType = (
  testType: TestingTool
): TestEventType => {
  switch (testType) {
    case "Scanner":
      return "scannerTestInitiated";
    case "Bin to Port":
      return "binToPortInitiated";
    case "PTL":
      return "ptlTestInitiated";
  }
};

export const getTestingToolStatusFromEventStatus = (
  eventStatus: EventStatus
): TestStepStatus => {
  switch (eventStatus) {
    case "Success":
      return "completed";
    case "Failed":
      return "error";
    case "Info":
    default:
      return "running";
  }
};
